// Inventory + WIP — 명세서 목표 "재고 정확도 99.5%" 충족
import { useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Card } from '../components/Card'
import { Button } from '../components/Button'
import { Counter } from '../components/Counter'
import { Skeleton, SkeletonRows } from '../components/Skeleton'
import { api } from '../lib/api'
import { useToast } from '../components/Toast'
import { useGlobalReveal } from '../hooks/useReveal'
import { useAuth, hasPerm } from '../lib/auth'
import { useT } from '../lib/i18n'

interface Location {
  location_id: string
  name: string
  kind: 'warehouse' | 'line' | 'staging' | 'quarantine' | 'virtual'
  parent_id: string | null
}

interface Balance {
  material_lot_id: string
  item_code: string
  vendor: string | null
  uom: string
  initial_qty: number
  on_hand_qty: number | null
  status: string
  location_id: string | null
  location_name: string | null
  location_kind: string | null
  standard_cost: number | null
  on_hand_value: number | null
}

interface Txn {
  txn_id: string
  material_lot_id: string
  item_code: string | null
  txn_type: 'IN' | 'OUT' | 'MOVE' | 'ADJUST' | 'ISSUE'
  qty_delta: number
  from_location: string | null
  to_location: string | null
  work_order_id: string | null
  note: string | null
  created_by: string | null
  created_at: string
}

interface AccuracyResp {
  window_days: number
  sample_count: number
  average_accuracy_pct: number | null
  target_pct: number
  meets_target: boolean
  recent: Array<{
    material_lot_id: string
    counted_qty: number
    system_qty: number
    accuracy_pct: number
    variance: number
    counted_at: string
  }>
}

interface WipLine {
  component_item_code: string
  uom: string
  required_qty: number
  issued_qty: number
  variance: number
  variance_pct: number | null
  status: 'short' | 'over' | 'ok'
}
interface WipWO {
  work_order_id: string
  line_id: string
  status: string
  item_code: string
  po_quantity: number
  lines: WipLine[] | Array<{ error: string }>
}

const TXN_BADGE: Record<Txn['txn_type'], string> = {
  IN: 'success', OUT: 'warn', MOVE: 'info', ADJUST: '', ISSUE: 'info',
}
const STATUS_BADGE: Record<WipLine['status'], string> = {
  short: 'danger', over: 'warn', ok: 'success',
}

const fmt = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : `₩${Math.round(n).toLocaleString('ko-KR')}`

// KRW 압축 표기: 1억 이상 "X.XX억", 1만 이상 "X,XXX만", 그 외 원래 숫자.
// KPI 카드처럼 좁은 영역에서 사용. 정확한 숫자는 title 속성으로 표시.
function formatKrwCompact(n: number): string {
  if (n >= 1e8) return `${(n / 1e8).toFixed(2)}억`
  if (n >= 1e4) return `${Math.round(n / 1e4).toLocaleString('ko-KR')}만`
  return n.toLocaleString('ko-KR')
}

export function InventoryPage() {
  useGlobalReveal()
  const toast = useToast()
  const { t } = useT()
  const { user } = useAuth()
  const canWrite = hasPerm(user, 'inventory', 'write')

  const [locations, setLocations] = useState<Location[] | null>(null)
  const [activeLoc, setActiveLoc] = useState<string>('')
  const [balance, setBalance] = useState<Balance[] | null>(null)
  const [txns, setTxns] = useState<Txn[] | null>(null)
  const [acc, setAcc] = useState<AccuracyResp | null>(null)
  const [wip, setWip] = useState<WipWO[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [countFor, setCountFor] = useState<{ lot: Balance; counted: string } | null>(null)

  const loadAll = async () => {
    try {
      const [locs, a, w, t] = await Promise.all([
        api.get<Location[]>('/inventory/locations'),
        api.get<AccuracyResp>('/inventory/accuracy?days=30'),
        api.get<WipWO[]>('/inventory/wip'),
        api.get<Txn[]>('/inventory/transactions?days=30'),
      ])
      setLocations(locs); setAcc(a); setWip(w); setTxns(t)
    } catch (e: any) { toast.push('error', e?.message ?? '조회 실패') }
  }
  useEffect(() => { loadAll() }, [])

  useEffect(() => {
    const q = activeLoc ? `?location_id=${activeLoc}` : ''
    api.get<Balance[]>(`/inventory/balance${q}`).then(setBalance).catch(() => setBalance([]))
  }, [activeLoc])

  const totalValue = useMemo(() =>
    balance?.reduce((a, b) => a + (b.on_hand_value ?? 0), 0) ?? 0,
    [balance])

  const submitCount = async () => {
    if (!countFor) return
    const q = Number(countFor.counted)
    if (!Number.isFinite(q) || q < 0) { toast.push('error', '실측 수량은 0 이상'); return }
    setBusy(true)
    try {
      const r = await api.post<{ accuracy_pct: number; variance: number }>('/inventory/count', {
        material_lot_id: countFor.lot.material_lot_id,
        counted_qty: q,
      })
      toast.push('success', `정확도 ${r.accuracy_pct.toFixed(2)}% · 차이 ${r.variance >= 0 ? '+' : ''}${r.variance}`)
      setCountFor(null)
      loadAll()
    } catch (e: any) { toast.push('error', e?.message ?? '실패') }
    finally { setBusy(false) }
  }

  return (
    <main id="main" className="container stack" style={{ paddingBlock: '2rem' }}>
      <h1 className="page-title reveal">
        <FontAwesomeIcon icon="warehouse" /> {t('inv.title')}
      </h1>
      <p className="muted reveal">{t('inv.intro')}</p>

      {/* KPI */}
      <section
        className="reveal"
        style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))' }}
      >
        <Card glass tilt={false} spotlight={false}>
          <span className={`badge ${acc?.meets_target ? 'success' : 'warn'}`}>
            <FontAwesomeIcon icon="scale-balanced" /> 재고 정확도 (30일)
          </span>
          <p className="mega-num" style={{ margin: '0.4rem 0 0' }}>
            {acc === null ? <Skeleton h={56} /> :
              acc.average_accuracy_pct === null ? '—' :
              <><Counter value={acc.average_accuracy_pct} decimals={2} suffix="%" /></>}
          </p>
          <p className="muted" style={{ margin: '0.2rem 0 0', fontSize: 'var(--fs-sm)' }}>
            목표 {acc?.target_pct ?? 99.5}% · 표본 {acc?.sample_count ?? 0}건
          </p>
        </Card>
        <Card glass tilt={false} spotlight={false}>
          <span className="badge info"><FontAwesomeIcon icon="boxes-stacked" /> 가용 자재 LOT</span>
          <p className="kpi-num">
            {balance ? <Counter value={balance.filter((b) => (b.on_hand_qty ?? 0) > 0).length} suffix=" 개" /> : <Skeleton h={36} />}
          </p>
        </Card>
        <Card glass tilt={false} spotlight={false}>
          <span className="badge"><FontAwesomeIcon icon="money-bill-trend-up" /> 재고 평가액</span>
          <p
            className="kpi-num"
            style={{ whiteSpace: 'nowrap' }}
            title={balance ? `₩${Math.round(totalValue).toLocaleString('ko-KR')}` : undefined}
          >
            {balance ? `₩${formatKrwCompact(Math.round(totalValue))}` : <Skeleton h={36} />}
          </p>
        </Card>
        <Card glass tilt={false} spotlight={false}>
          <span className="badge"><FontAwesomeIcon icon="warehouse" /> 위치</span>
          <p className="kpi-num">
            {locations ? <Counter value={locations.length} suffix=" 곳" /> : <Skeleton h={36} />}
          </p>
        </Card>
      </section>

      {/* 위치 필터 + 잔량 테이블 */}
      <Card glass tilt={false} spotlight={false} className="reveal" as="section" aria-labelledby="bal-h">
        <div className="row space-between" style={{ flexWrap: 'wrap', gap: '0.6rem' }}>
          <h2 id="bal-h" style={{ fontSize: 'var(--fs-lg)', marginBottom: 0 }}>잔량</h2>
          <div className="row" style={{ gap: '0.3rem', flexWrap: 'wrap' }}>
            <Button variant={activeLoc === '' ? 'accent' : 'ghost'} magnetic={false} onClick={() => setActiveLoc('')}>전체</Button>
            {locations?.map((l) => (
              <Button key={l.location_id}
                variant={activeLoc === l.location_id ? 'accent' : 'ghost'}
                magnetic={false}
                onClick={() => setActiveLoc(l.location_id)}>
                {l.name}
              </Button>
            ))}
          </div>
        </div>
        {!balance ? <SkeletonRows rows={4} /> : balance.length === 0 ? (
          <div className="empty-state" role="status">
            <FontAwesomeIcon icon="warehouse" className="icon-lg" />
            <p className="muted" style={{ margin: 0 }}>해당 위치에 잔량 LOT 없음.</p>
          </div>
        ) : (
          <div className="table-wrap" style={{ '--table-min-w': '880px' } as React.CSSProperties}>
            <table className="table" aria-label="자재 LOT 잔량">
              <thead>
                <tr>
                  <th>LOT</th><th>품번</th><th>위치</th><th>상태</th>
                  <th>초기 입고</th><th>현재 잔량</th><th>평가액</th><th></th>
                </tr>
              </thead>
              <tbody>
                {balance.map((b) => {
                  const onHand = b.on_hand_qty ?? 0
                  const issued = b.initial_qty - onHand
                  const consumedPct = b.initial_qty > 0 ? (issued / b.initial_qty) * 100 : 0
                  return (
                    <tr key={b.material_lot_id}>
                      <td className="mono">{b.material_lot_id}</td>
                      <td>{b.item_code}</td>
                      <td>
                        {b.location_name ? <span className="badge info">{b.location_name}</span> : <span className="muted">—</span>}
                      </td>
                      <td><span className={`badge ${b.status === 'available' ? 'success' : ''}`}>{b.status}</span></td>
                      <td className="mono muted">{b.initial_qty} {b.uom}</td>
                      <td className="mono">
                        <strong>{onHand}</strong> {b.uom}
                        <span className="muted" style={{ marginLeft: 6, fontSize: 'var(--fs-xs)' }}>
                          ({consumedPct.toFixed(0)}% 소비)
                        </span>
                      </td>
                      <td className="mono">{fmt(b.on_hand_value)}</td>
                      <td>
                        <Button variant="ghost" magnetic={false} disabled={!canWrite}
                          onClick={() => setCountFor({ lot: b, counted: String(onHand) })}
                          aria-label={`${b.material_lot_id} 실사`}>
                          <FontAwesomeIcon icon="scale-balanced" />
                          <span>실사</span>
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* WIP variance */}
      <Card glass tilt={false} spotlight={false} className="reveal" as="section" aria-labelledby="wip-h">
        <h2 id="wip-h" style={{ fontSize: 'var(--fs-lg)' }}>WIP — 작업지시별 자재 issue vs BOM 요구</h2>
        {!wip ? <SkeletonRows rows={3} /> : wip.length === 0 ? (
          <p className="muted">진행 중 작업지시 없음.</p>
        ) : (
          <div className="stack">
            {wip.map((w) => (
              <div key={w.work_order_id} style={{ padding: '0.6rem 0.8rem', borderRadius: 'var(--radius-sm)', background: 'var(--color-bg-soft)' }}>
                <div className="row space-between" style={{ marginBottom: '0.5rem' }}>
                  <div>
                    <strong className="mono">{w.work_order_id}</strong>{' '}
                    <span className="muted">· {w.item_code} × {w.po_quantity} · {w.line_id}</span>
                  </div>
                  <span className={`badge ${w.status === 'running' ? 'info' : ''}`}>{w.status}</span>
                </div>
                {Array.isArray(w.lines) && w.lines.length === 0 ? (
                  <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>BOM 미정의</p>
                ) : w.status === 'created' ? (
                  <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
                    작업 시작 전 — 자재 issue 기록 없음 (요구량 {w.lines?.length ?? 0}개 자재)
                  </p>
                ) : (
                  <div className="table-wrap" style={{ '--table-min-w': '600px' } as React.CSSProperties}>
                    <table className="table" aria-label={`${w.work_order_id} WIP 변동`}>
                      <thead>
                        <tr>
                          <th>자재</th><th>요구</th><th>이슈</th><th>차이</th><th>%</th><th>상태</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(w.lines as WipLine[]).map((l) => (
                          <tr key={l.component_item_code}>
                            <td className="mono">{l.component_item_code}</td>
                            <td className="mono">{l.required_qty.toFixed(2)} {l.uom}</td>
                            <td className="mono">{l.issued_qty.toFixed(2)}</td>
                            <td className="mono">{l.variance >= 0 ? '+' : ''}{l.variance.toFixed(2)}</td>
                            <td className="mono">{l.variance_pct === null ? '—' : `${l.variance_pct >= 0 ? '+' : ''}${l.variance_pct.toFixed(1)}%`}</td>
                            <td><span className={`badge ${STATUS_BADGE[l.status]}`}>{l.status}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* 최근 트랜잭션 */}
      <Card glass tilt={false} spotlight={false} className="reveal" as="section" aria-labelledby="txn-h">
        <h2 id="txn-h" style={{ fontSize: 'var(--fs-lg)' }}>최근 30일 트랜잭션</h2>
        {!txns ? <SkeletonRows rows={3} /> : txns.length === 0 ? (
          <p className="muted">트랜잭션 없음.</p>
        ) : (
          <div className="table-wrap" style={{ '--table-min-w': '760px' } as React.CSSProperties}>
            <table className="table" aria-label="재고 트랜잭션">
              <thead>
                <tr><th>시각</th><th>타입</th><th>LOT</th><th>품번</th><th>수량</th><th>위치</th><th>비고</th></tr>
              </thead>
              <tbody>
                {txns.slice(0, 30).map((t) => (
                  <tr key={t.txn_id}>
                    <td>{new Date(t.created_at).toLocaleString('ko-KR')}</td>
                    <td><span className={`badge ${TXN_BADGE[t.txn_type]}`}>{t.txn_type}</span></td>
                    <td className="mono">{t.material_lot_id}</td>
                    <td>{t.item_code ?? '—'}</td>
                    <td className="mono">{t.qty_delta > 0 ? '+' : ''}{t.qty_delta}</td>
                    <td className="muted">
                      {t.from_location && <>{t.from_location}{t.to_location && ' → '}</>}
                      {t.to_location ?? (t.txn_type === 'OUT' || t.txn_type === 'ISSUE' ? '소비' : '')}
                    </td>
                    <td style={{ maxWidth: 240 }}>{t.note ?? (t.work_order_id ?? '')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* 실사 모달 */}
      {countFor && (
        <div role="dialog" aria-modal="true" aria-label="재고 실사"
          onClick={(e) => { if (e.target === e.currentTarget) setCountFor(null) }}
          style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'grid', placeItems: 'center', padding: '1rem' }}
        >
          <Card glass tilt={false} spotlight={false} style={{ width: 'min(440px, 100%)' }}>
            <h2 style={{ fontSize: 'var(--fs-lg)' }}>실사 — {countFor.lot.material_lot_id}</h2>
            <p className="muted" style={{ marginBottom: '1rem', fontSize: 'var(--fs-sm)' }}>
              {countFor.lot.item_code} · 시스템 잔량 <span className="mono">{countFor.lot.on_hand_qty ?? 0} {countFor.lot.uom}</span>
            </p>
            <div className="field">
              <label htmlFor="cnt">실측 수량 ({countFor.lot.uom})</label>
              <input id="cnt" type="number" min="0" step="0.01"
                value={countFor.counted}
                onChange={(e) => setCountFor({ ...countFor, counted: e.target.value })} />
            </div>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <Button variant="ghost" magnetic={false} onClick={() => setCountFor(null)}>취소</Button>
              <Button variant="accent" magnetic={false}
                disabled={busy || !countFor.counted}
                onClick={submitCount}>
                <FontAwesomeIcon icon={busy ? 'circle-notch' : 'scale-balanced'} spin={busy} />
                <span>실사 등록</span>
              </Button>
            </div>
          </Card>
        </div>
      )}
    </main>
  )
}
